import { defineStore } from "pinia";
import { ApiError, apiFetch } from "@/services/apiClient";
import { sanitizeUnlockCodeInput } from "@/helpers/unlock-errors";
import {
  clearPlexRecentlyAdded,
  clearContinueWatchingCards,
} from "@/services/libraryCache";
import router from "@/router";

// Module-local cache for the in-flight hydrate promise. Kept off the reactive
// state so that repeated reads return exactly the same Promise reference (Pinia
// proxy-wraps values stored on `state`, which breaks referential equality).
let hydratePromise = null;

// Auth store is the single source of truth for the SPA's gate-state. The cookie
// (`__Host-putio_device` in prod, `putio_device` in dev) is the actual source of
// truth — this store just mirrors what the server says via `/api/auth/status`.
//
// IMPORTANT: this store must NOT be persisted. Persisted state goes stale the
// moment an admin revokes the token; we always re-hydrate from the server.
export const useAuthStore = defineStore("auth", {
  state: () => ({
    status: "loading", // 'loading' | 'locked' | 'unlocked' | 'admin'
    label: null,
    deviceName: null,
    // Opaque per-device identifier from /api/auth/status, used by
    // seriesProgress + playbackPosition to namespace localStorage so
    // two device cookies sharing one browser don't cross-leak Continue
    // Watching. Null while loading / locked / on legacy backends.
    deviceKey: null,
    tier: null, // 'full' | 'library' | null (when locked) — legacy
    permissions: [], // granular permission strings from server
    hydrating: false,
    // First-visit onboarding flag from /api/auth/status. `true` means the
    // device has set its preferences (or been auto-onboarded, e.g. admins);
    // `false` means it hasn't yet and App.vue should show the homepage
    // PreferencesPrompt. Defaults to `true` so the prompt never shows
    // pre-hydrate / during 'loading' — flips to `false` only when the server
    // explicitly says so.
    onboarded: true,
    // Feature 74: this invite may only see the Put.io items an admin
    // featured for it. Purely a UI hint — the real boundary is enforced
    // server-side at the /api/putio proxy. Defaults false so nothing is
    // hidden pre-hydrate on a normal invite.
    restrictedToFeatured: false,
  }),
  getters: {
    isAdmin: (state) => state.status === "admin",
    isUnlocked: (state) =>
      state.status === "unlocked" || state.status === "admin",
    // Granular permission check. Admins implicitly have every permission.
    // Use this for any new feature gates; the convenience getters below
    // exist for back-compat with existing call sites.
    hasPermission: (state) => (name) => {
      if (state.status === "admin") return true;
      return (
        Array.isArray(state.permissions) && state.permissions.includes(name)
      );
    },
    // Convenience getters derived from the granular permissions list.
    // `canBrowseTorrents` historically gated TMDB discovery + downloads;
    // it now maps to `discover`. Component code that needs a different
    // permission should call `hasPermission(...)` directly.
    canBrowseTorrents() {
      return this.hasPermission("discover");
    },
    // Feature 74: a featured-only invite is blocked from transfers and from
    // every library mutation at the proxy, so the affordances that lead there
    // are folded off here rather than at each of the ~dozen call sites. This
    // is presentation only — the server is the boundary.
    canAccessDownloads() {
      return !this.restrictedToFeatured && this.hasPermission("add-transfers");
    },
    canAddTransfers() {
      return !this.restrictedToFeatured && this.hasPermission("add-transfers");
    },
    canLinkDevice() {
      return this.hasPermission("link-tv");
    },
    // Browse Put.io files + play media. Baseline-ish, but now explicit.
    canAccessLibrary() {
      return this.hasPermission("library");
    },
    canBrowseLiveTv() {
      return this.hasPermission("live-tv");
    },
    // TorBox debrid playback is bundled with the discover permission --
    // anyone who can browse Discover can also instant-play / debrid.
    canUseTorbox() {
      return this.hasPermission("discover");
    },
    canStartWatchParty() {
      return this.hasPermission("watch-party");
    },
    canPairRemote() {
      return this.hasPermission("remote-control");
    },
    canDeleteMedia() {
      return !this.restrictedToFeatured && this.hasPermission("add-transfers");
    },
    // Add / remove Plex accounts and Jellyfin servers. Operator-level —
    // not in the default invite permission set; admin grants it
    // explicitly via AdminTokensView.
    canConnectExternalSources() {
      return this.hasPermission("connect-external-sources");
    },
    // Browse + play from already-connected external sources. Default
    // for new invites (Feature 26). `connect-external-sources` implies
    // this on the server side, so admins who carry connect also pass
    // here without an extra grant.
    canUseExternalSources() {
      return (
        this.hasPermission("use-external-sources") ||
        this.hasPermission("connect-external-sources")
      );
    },
    // Grant specific other invite tokens read+stream access to one of
    // their own connected Plex / Jellyfin servers (Feature 28). Does NOT
    // imply connect/use; the sharer needs at least `connect-external-sources`
    // to own a source in the first place, and the sharee needs
    // `use-external-sources` to actually play.
    canShareExternalSources() {
      return this.hasPermission("share-external-sources");
    },
    // Put.io account details (username, storage, bandwidth, plan, parallel
    // downloads) are admin-only. Showing them to regular invite users
    // leaks the underlying put.io account identity / usage to anyone
    // holding an invite -- they're "host" info, not user info.
    canViewAccountStats() {
      return this.status === "admin";
    },
  },
  actions: {
    /**
     * Fetch the current auth state from the server.
     * Memoized: repeated calls return the same in-flight promise until
     * `markLocked()` or `logout()` clears the module-local cache.
     * Pass `force=true` to bypass the cache (used after side-effects
     * that change the server-side state — e.g. completing the welcome
     * wizard flips `onboarded` and the SPA needs a fresh read to
     * unmount the modal).
     */
    hydrate(force = false) {
      if (force) {
        hydratePromise = null;
      }
      if (hydratePromise) {
        return hydratePromise;
      }
      this.hydrating = true;
      hydratePromise = (async () => {
        // Transient-error retry: network blips, slow proxy warm-ups, or
        // a momentary 5xx from /api/auth/status used to immediately
        // flip the store to "locked" and bounce the user to /locked on
        // refresh — even though their cookie was still valid (a second
        // refresh would let them through). Retry the auth probe up to
        // 3 times with a short backoff before failing closed. Explicit
        // 401s (server-side cookie invalid / device revoked) DON'T
        // retry — apiFetch already handled markLocked + redirect, and
        // there's no point hammering on a definitive denial.
        const RETRY_GAPS_MS = [200, 600]; // 1 initial + 2 retries
        let body = null;
        let fatalErr = null;
        for (let attempt = 0; attempt <= RETRY_GAPS_MS.length; attempt += 1) {
          try {
            body = await apiFetch("/api/auth/status");
            break;
          } catch (err) {
            // 401 = definitive lock from the server. Don't retry; let
            // the existing fail-closed branch below run on the next
            // iteration boundary.
            if (err instanceof ApiError && err.status === 401) {
              fatalErr = err;
              break;
            }
            // Transient (network rejection, 5xx, parse). Sleep + retry,
            // unless we've used the last attempt.
            if (attempt === RETRY_GAPS_MS.length) {
              fatalErr = err;
              break;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, RETRY_GAPS_MS[attempt]));
          }
        }
        try {
          if (fatalErr) throw fatalErr;
          // Expected shape: { state, label?, deviceName?, tier? }
          const state = body?.state;
          const ALL_PERMS = [
            "library",
            "add-transfers",
            "discover",
            "live-tv",
            "link-tv",
            "remote-control",
            "watch-party",
            "connect-external-sources",
          ];
          if (state === "admin") {
            this.status = "admin";
            this.label = body.label ?? null;
            this.deviceName = body.deviceName ?? null;
            this.deviceKey = body.deviceKey ?? null;
            this.tier = "full";
            // Admins implicitly have all permissions; mirror it on state
            // so checks that don't go through hasPermission() also pass.
            this.permissions = Array.isArray(body.permissions)
              ? body.permissions
              : ALL_PERMS;
            // Admins skip the welcome wizard.
            this.onboarded = true;
            // Admins are never scoped to a curated slice (Feature 74).
            this.restrictedToFeatured = false;
          } else if (state === "unlocked") {
            this.status = "unlocked";
            this.label = body.label ?? null;
            this.deviceName = body.deviceName ?? null;
            this.deviceKey = body.deviceKey ?? null;
            this.tier = body.tier === "library" ? "library" : "full";
            // Permissions array is authoritative. Fall back to a tier
            // mapping only when the server didn't send one (pre-perms
            // backend versions).
            if (Array.isArray(body.permissions)) {
              this.permissions = body.permissions;
            } else {
              this.permissions = this.tier === "library" ? [] : ALL_PERMS;
            }
            // Pre-v12 servers don't send `onboarded`; treat missing as
            // "already onboarded" so existing users don't get surprised.
            this.onboarded = body.onboarded !== false;
            this.restrictedToFeatured = body.restrictedToFeatured === true;
          } else {
            // 'locked' or anything unexpected — fail closed
            this.status = "locked";
            this.label = null;
            this.deviceName = null;
            this.deviceKey = null;
            this.tier = null;
            this.permissions = [];
            this.onboarded = true;
            this.restrictedToFeatured = false;
          }
        } catch (err) {
          // Any error (network, 5xx, parse) → fail closed. The apiFetch 401/403
          // handler will have already called markLocked(), but be defensive.
          this.status = "locked";
          this.label = null;
          this.deviceName = null;
          this.deviceKey = null;
          this.tier = null;
          this.restrictedToFeatured = false;
        } finally {
          this.hydrating = false;
        }
      })();
      return hydratePromise;
    },

    async setDeviceName(name) {
      const body = await apiFetch("/api/auth/device/name", {
        method: "POST",
        json: { name },
      });
      if (body && typeof body.deviceName === "string") {
        this.deviceName = body.deviceName;
      } else {
        this.deviceName = name;
      }
      return this.deviceName;
    },

    // ---- Per-device recovery credentials (DB v16) -----------------
    // Optional username + password tied to the current device. Setting
    // them lets the user recover via /api/auth/login if they ever lose
    // the invite link. The invite link itself always works regardless.

    async getCredentials() {
      try {
        return await apiFetch("/api/auth/credentials");
      } catch {
        return null;
      }
    },

    async setCredentials({ username, password }) {
      // Returns { ok, username } on success. Throws ApiError on
      // validation failure / username conflict — caller surfaces the
      // error.body.error code in the form UI.
      return apiFetch("/api/auth/credentials", {
        method: "POST",
        json: { username, password },
      });
    },

    async clearCredentials() {
      return apiFetch("/api/auth/credentials", { method: "DELETE" });
    },

    async loginWithCredentials({ username, password }) {
      try {
        await apiFetch("/api/auth/login", {
          method: "POST",
          json: { username, password },
        });
        hydratePromise = null;
        await this.hydrate();
        if (this.status === "unlocked" || this.status === "admin") {
          return { ok: true };
        }
      } catch (err) {
        const error =
          err instanceof ApiError && typeof err.body?.error === "string"
            ? err.body.error
            : "invalid_credentials";
        return { ok: false, error };
      }
      return { ok: false, error: "invalid_credentials" };
    },

    async unlock(code) {
      const normalized = sanitizeUnlockCodeInput(code);
      if (!normalized) {
        this.markLocked();
        return { ok: false, error: "invalid" };
      }

      try {
        await apiFetch("/api/auth/unlock", {
          method: "POST",
          json: { code: normalized },
        });
        hydratePromise = null;
        await this.hydrate();
        if (this.status === "unlocked" || this.status === "admin") {
          return { ok: true };
        }
      } catch (err) {
        const error =
          err instanceof ApiError && typeof err.body?.error === "string"
            ? err.body.error
            : "invalid";
        this.markLocked();
        return { ok: false, error };
      }

      this.markLocked();
      return { ok: false, error: "invalid" };
    },

    async logout() {
      try {
        await apiFetch("/api/auth/logout", { method: "POST" });
      } catch {
        // Logout should always clear local state + redirect, even if the
        // server call fails (network down, already-revoked cookie, etc.).
      }
      // Drop this device's cached library data BEFORE clearing the deviceKey
      // (the cache key is namespaced by it) so a logout on a shared device
      // doesn't leave the previous user's Plex titles or Continue Watching
      // cards in localStorage.
      clearPlexRecentlyAdded(this.deviceKey);
      clearContinueWatchingCards(this.deviceKey);
      this.status = "locked";
      this.label = null;
      this.deviceName = null;
      this.deviceKey = null;
      this.tier = null;
      this.permissions = [];
      hydratePromise = null;
      try {
        router.push({ name: "locked" });
      } catch {
        // If the router doesn't have a 'locked' route yet (Task 8), swallow.
      }
    },

    /**
     * Called by the apiClient 401/403 handler. Flips to locked and clears the
     * cached hydrate promise so the next guard re-checks with the server.
     */
    markLocked() {
      // Same hygiene as logout(): clear the device-scoped library caches
      // before dropping the deviceKey they're namespaced by.
      clearPlexRecentlyAdded(this.deviceKey);
      clearContinueWatchingCards(this.deviceKey);
      this.status = "locked";
      this.label = null;
      this.deviceName = null;
      this.deviceKey = null;
      this.tier = null;
      this.permissions = [];
      hydratePromise = null;
    },
  },
});
